import ReactGA from 'react-ga4';
import mixpanel from 'mixpanel-browser';
import axios from 'axios';

import { apiUrl } from '@/api/config';
import { getAuthToken, getUserData, getPortalType } from '@/utils/auth';
import { getAnonymousUserPreferences } from '@/utils/anonymousAuth';

const ANONYMOUS_ID_KEY = 'budlee_anonymous_id';
const SESSION_ID_KEY = 'budlee_analytics_session_id';
const SESSION_STARTED_KEY = 'budlee_analytics_session_started';
const FIRST_TOUCH_KEY = 'budlee_first_touch';
const LAST_TOUCH_KEY = 'budlee_last_touch';
const EVENT_COUNTER_KEY = 'budlee_analytics_event_counter';
const FLUSH_INTERVAL_MS = 5000;
const HEARTBEAT_INTERVAL_MS = 15000;
const IDLE_TIMEOUT_MS = 60000;

const state = {
  initialized: false,
  pendingEvents: [],
  flushTimer: null,
  heartbeatTimer: null,
  sessionStartedAt: null,
  entryPath: null,
  currentPath: null,
  lastInteractionAt: Date.now(),
  lastHeartbeatAt: Date.now(),
  engagedMs: 0,
  lastFlushAt: 0,
};

const isBrowser = typeof window !== 'undefined';

const getCurrentPath = () => {
  if (!isBrowser) return '';
  const hashPath = window.location.hash ? window.location.hash.split('?')[0].replace('#', '') : '';
  return hashPath || window.location.pathname || '';
};

const getUrlSearchParams = () => {
  if (!isBrowser) return new URLSearchParams('');
  const hash = window.location.hash || '';
  const queryFromHash = hash.includes('?') ? hash.split('?')[1] : '';
  const fallbackQuery = window.location.search ? window.location.search.replace(/^\?/, '') : '';
  return new URLSearchParams(queryFromHash || fallbackQuery);
};

const readJson = (storage, key, fallback = null) => {
  if (!storage) return fallback;
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (storage, key, value) => {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {}
};

const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `anon_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const generateEventId = () => {
  if (!isBrowser) return generateId();
  try {
    const current = Number(localStorage.getItem(EVENT_COUNTER_KEY) || '0') + 1;
    localStorage.setItem(EVENT_COUNTER_KEY, String(current));
    return `evt_${Date.now()}_${current}`;
  } catch {
    return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }
};

export const getAnonymousId = () => {
  if (!isBrowser) return null;
  try {
    const existing = localStorage.getItem(ANONYMOUS_ID_KEY);
    if (existing) return existing;
    const created = generateId();
    localStorage.setItem(ANONYMOUS_ID_KEY, created);
    return created;
  } catch {
    return generateId();
  }
};

export const getAnalyticsSessionId = () => {
  if (!isBrowser) return null;
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const created = generateId();
    sessionStorage.setItem(SESSION_ID_KEY, created);
    return created;
  } catch {
    return generateId();
  }
};

const getCurrentPortal = () => {
  if (!isBrowser) return 'student';
  return getPortalType(getCurrentPath());
};

const getTouchPayload = () => {
  if (!isBrowser) return {};
  const latest = readJson(localStorage, LAST_TOUCH_KEY, {}) || {};
  return {
    landing_path: latest.landing_path || getCurrentPath(),
    landing_variant: latest.landing_variant || '',
    experiment_id: latest.experiment_id || '',
    referrer: latest.referrer || document.referrer || '',
    utm_source: latest.utm_source || '',
    utm_medium: latest.utm_medium || '',
    utm_campaign: latest.utm_campaign || '',
    utm_term: latest.utm_term || '',
    utm_content: latest.utm_content || '',
  };
};

const getFirstTouchPayload = () => {
  if (!isBrowser) return {};
  const first = readJson(localStorage, FIRST_TOUCH_KEY, {}) || {};
  return {
    landing_path: first.landing_path || getCurrentPath(),
    landing_variant: first.landing_variant || '',
    experiment_id: first.experiment_id || '',
    referrer: first.referrer || document.referrer || '',
    utm_source: first.utm_source || '',
    utm_medium: first.utm_medium || '',
    utm_campaign: first.utm_campaign || '',
    utm_term: first.utm_term || '',
    utm_content: first.utm_content || '',
  };
};

const captureTouch = () => {
  if (!isBrowser) return;
  const params = getUrlSearchParams();
  const touch = {
    landing_path: getCurrentPath(),
    landing_variant: params.get('landing_variant') || params.get('variant') || params.get('lp_variant') || '',
    experiment_id: params.get('experiment_id') || params.get('exp_id') || params.get('ab_test') || '',
    referrer: document.referrer || '',
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_term: params.get('utm_term') || '',
    utm_content: params.get('utm_content') || '',
  };
  const hasCampaignData = Object.values(touch).some(Boolean);
  if (!hasCampaignData) return;

  const firstTouch = readJson(localStorage, FIRST_TOUCH_KEY, null);
  if (!firstTouch) {
    writeJson(localStorage, FIRST_TOUCH_KEY, touch);
  }
  writeJson(localStorage, LAST_TOUCH_KEY, touch);
};

const isGuestSession = () => {
  if (!isBrowser) return false;
  try {
    return localStorage.getItem('is_guest_session') === 'true';
  } catch {
    return false;
  }
};

const getStoredId = (key) => {
  if (!isBrowser) return null;
  try {
    const value = localStorage.getItem(key);
    return value ? Number(value) : null;
  } catch {
    return null;
  }
};

const resolveAnalyticsIdentityContext = (portal) => {
  const user = getUserData(portal) || {};
  const anonymousPrefs = isGuestSession() ? (getAnonymousUserPreferences() || {}) : {};

  const boardId =
    user.board?.id
    ?? user.board_id
    ?? anonymousPrefs.boardId
    ?? anonymousPrefs.board_id
    ?? getStoredId('student_board_id')
    ?? null;
  const boardName =
    user.board?.board_name
    ?? user.board?.board_code
    ?? user.board_name
    ?? anonymousPrefs.boardName
    ?? anonymousPrefs.board_name
    ?? (isBrowser ? localStorage.getItem('student_board_name') : null)
    ?? null;
  const gradeId =
    user.grade?.id
    ?? user.grade_id
    ?? anonymousPrefs.gradeId
    ?? anonymousPrefs.grade_id
    ?? getStoredId('student_grade_id')
    ?? null;
  const gradeName =
    user.grade?.grade_name
    ?? user.grade_name
    ?? anonymousPrefs.gradeName
    ?? anonymousPrefs.grade_name
    ?? null;

  return {
    user,
    boardId,
    boardName,
    gradeId,
    gradeName,
  };
};

const buildUserContext = () => {
  const portal = getCurrentPortal();
  const {
    user,
    boardId,
    boardName,
    gradeId,
    gradeName,
  } = resolveAnalyticsIdentityContext(portal);

  return {
    portal,
    user_type: user.user_type ?? user.type ?? (portal === 'admin' ? 'superadmin' : portal) ?? null,
    board_id: boardId,
    board_name: boardName,
    grade_id: gradeId,
    grade_name: gradeName,
    country: user.country ?? user.country_code ?? null,
    platform: user.platform ?? null,
    app_type: user.app_type ?? null,
    device_name: user.device_name ?? null,
    subscription_status: user.subscription_status ?? null,
  };
};

const getAuthHeaders = () => {
  const portal = getCurrentPortal();
  const token = getAuthToken(portal);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getPageForEvent = (params = {}) => {
  if (params.page) return params.page;
  if (state.currentPath) return state.currentPath;
  if (!isBrowser) return '';
  return getCurrentPath();
};

const getEventFeature = (name, params = {}) => {
  if (params.feature) return params.feature;
  const eventName = String(name || '').toLowerCase();
  const page = String(getPageForEvent(params) || '').toLowerCase();

  if (eventName.startsWith('chat_') || page.includes('/student/chat') || page === 'chat') return 'chat';
  if (eventName.startsWith('quest_') || eventName.startsWith('test_') || page.includes('/student/quests') || page === 'quests') return 'quest';
  if (eventName.startsWith('learn_') || eventName === 'learning_path_started' || page.includes('/student/learn') || page === 'learn') return 'learn';
  if (eventName.startsWith('content_') || page.includes('curiosity')) return 'curiosity';
  if (eventName.startsWith('checkout_') || eventName.startsWith('subscription_') || params.billing_cycle) return 'monetization';
  if (eventName.startsWith('signup_') || eventName.startsWith('login_')) return 'auth';
  if (page.includes('/admin')) return 'admin_dashboard';
  return '';
};

const buildSessionPayload = ({ endSession = false } = {}) => ({
  started_at: state.sessionStartedAt ? new Date(state.sessionStartedAt).toISOString() : new Date().toISOString(),
  last_seen_at: new Date().toISOString(),
  ended_at: endSession ? new Date().toISOString() : null,
  engaged_time_seconds: Math.round(state.engagedMs / 1000),
  entry_path: state.entryPath || '',
  exit_path: endSession ? (state.currentPath || getCurrentPath()) : '',
  is_guest: isGuestSession(),
});

const shouldTrackNetworkUrl = (url) => {
  if (!url || !isBrowser) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    const path = parsed.pathname || '';
    return path.startsWith('/api/');
  } catch {
    return String(url).includes('/api/');
  }
};

const isStreamingFetchRequest = (input, init = {}) => {
  const requestHeaders = new Headers(
    init.headers || (typeof Request !== 'undefined' && input instanceof Request ? input.headers : {})
  );
  const accept = String(requestHeaders.get('Accept') || '').toLowerCase();
  return accept.includes('text/event-stream');
};

const buildPayload = ({ events = [], endSession = false } = {}) => ({
  anonymous_id: getAnonymousId(),
  session_id: getAnalyticsSessionId(),
  touch: getTouchPayload(),
  first_touch: getFirstTouchPayload(),
  last_touch: getTouchPayload(),
  user_context: buildUserContext(),
  session: buildSessionPayload({ endSession }),
  events,
});

const flushProductAnalytics = async ({ endSession = false } = {}) => {
  if (!isBrowser) return;
  if (!state.pendingEvents.length && !endSession) return;

  const eventsToSend = [...state.pendingEvents];
  state.pendingEvents = [];
  const payload = buildPayload({ events: eventsToSend, endSession });

  try {
    await fetch(apiUrl('superadmin/analytics/ingest/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
      keepalive: endSession,
    });
    state.lastFlushAt = Date.now();
  } catch (error) {
    state.pendingEvents = [...eventsToSend, ...state.pendingEvents].slice(-100);
    if (!endSession) {
      console.warn('Product analytics flush failed', error);
    }
  }
};

const scheduleFlush = () => {
  if (state.flushTimer) return;
  state.flushTimer = window.setTimeout(async () => {
    state.flushTimer = null;
    await flushProductAnalytics();
  }, FLUSH_INTERVAL_MS);
};

const captureEngagedTime = () => {
  const now = Date.now();
  const userIsActive = now - state.lastInteractionAt <= IDLE_TIMEOUT_MS;
  const visible = !document.hidden;

  if (visible && userIsActive) {
    state.engagedMs += Math.max(0, now - state.lastHeartbeatAt);
  }
  state.lastHeartbeatAt = now;
};

const heartbeat = async () => {
  captureEngagedTime();
  if (Date.now() - state.lastFlushAt >= FLUSH_INTERVAL_MS) {
    await flushProductAnalytics();
  }
};

const markInteraction = () => {
  state.lastInteractionAt = Date.now();
};

const installSessionListeners = () => {
  if (!isBrowser || state.heartbeatTimer) return;

  ['click', 'keydown', 'scroll', 'touchstart', 'mousemove'].forEach((eventName) => {
    window.addEventListener(eventName, markInteraction, { passive: true });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      captureEngagedTime();
      flushProductAnalytics({ endSession: false });
    } else {
      state.lastHeartbeatAt = Date.now();
      markInteraction();
    }
  });

  const endSession = () => {
    captureEngagedTime();
    flushProductAnalytics({ endSession: true });
  };

  window.addEventListener('pagehide', endSession);
  window.addEventListener('beforeunload', endSession);

  state.heartbeatTimer = window.setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
};

const installAxiosAnalyticsInterceptors = () => {
  if (axios.__product_analytics_interceptor_installed) return;

  const reqTimes = new Map();

  axios.interceptors.request.use((config) => {
    try {
      const url = String(config.url || '');
      if (url.includes('superadmin/analytics/ingest/') || !shouldTrackNetworkUrl(url)) return config;
      const key = `${config.method || 'get'}::${url}::${Math.random()}`;
      reqTimes.set(key, Date.now());
      config.__analytics_key = key;
    } catch {}
    return config;
  }, (error) => Promise.reject(error));

  axios.interceptors.response.use((response) => {
    try {
      const config = response.config || {};
      const url = String(config.url || '');
      if (!url.includes('superadmin/analytics/ingest/') && shouldTrackNetworkUrl(url)) {
        const key = config.__analytics_key;
        const startedAt = key ? reqTimes.get(key) : null;
        const duration = startedAt ? Math.max(0, Date.now() - startedAt) : null;
        const method = String(config.method || 'get').toUpperCase();
        queueProductEvent('api_request', {
          page: getPageForEvent(),
          api_method: method,
          api_url: url,
          status: response.status,
          duration_ms: duration,
          api_transport: 'axios',
        });
        if (key) reqTimes.delete(key);
      }
    } catch {}
    return response;
  }, (error) => {
    try {
      const config = error.config || {};
      const url = String(config.url || '');
      if (!url.includes('superadmin/analytics/ingest/') && shouldTrackNetworkUrl(url)) {
        const method = String(config.method || 'get').toUpperCase();
        const key = config.__analytics_key;
        const startedAt = key ? reqTimes.get(key) : null;
        const duration = startedAt ? Math.max(0, Date.now() - startedAt) : null;
        queueProductEvent('api_error', {
          page: getPageForEvent(),
          api_method: method,
          api_url: url,
          status: error.response?.status ?? null,
          duration_ms: duration,
          error: String(error.message || error),
          api_transport: 'axios',
        });
        if (key) reqTimes.delete(key);
      }
    } catch {}
    return Promise.reject(error);
  });

  axios.__product_analytics_interceptor_installed = true;
};

const installFetchAnalyticsInterceptor = () => {
  if (!isBrowser || window.__product_analytics_fetch_installed || typeof window.fetch !== 'function') return;
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    const method = String(
      init.method ||
      (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET')
    ).toUpperCase();

    if (
      url.includes('superadmin/analytics/ingest/') ||
      !shouldTrackNetworkUrl(url) ||
      isStreamingFetchRequest(input, init)
    ) {
      return nativeFetch(input, init);
    }

    const startedAt = Date.now();
    try {
      const response = await nativeFetch(input, init);
      queueProductEvent('api_request', {
        page: getPageForEvent(),
        api_method: method,
        api_url: url,
        status: response.status,
        duration_ms: Math.max(0, Date.now() - startedAt),
        api_transport: 'fetch',
      });
      return response;
    } catch (error) {
      queueProductEvent('api_error', {
        page: getPageForEvent(),
        api_method: method,
        api_url: url,
        status: null,
        duration_ms: Math.max(0, Date.now() - startedAt),
        error: String(error?.message || error),
        api_transport: 'fetch',
      });
      throw error;
    }
  };

  window.__product_analytics_fetch_installed = true;
};

export const initProductAnalytics = () => {
  if (!isBrowser || state.initialized) return;

  captureTouch();
  state.initialized = true;
  state.sessionStartedAt = Date.now();
  state.lastHeartbeatAt = Date.now();
  state.lastInteractionAt = Date.now();
  state.currentPath = getCurrentPath();
  state.entryPath = state.currentPath;

  installAxiosAnalyticsInterceptors();
  installFetchAnalyticsInterceptor();
  installSessionListeners();

  try {
    const alreadyStarted = sessionStorage.getItem(SESSION_STARTED_KEY);
    if (!alreadyStarted) {
      sessionStorage.setItem(SESSION_STARTED_KEY, 'true');
      queueProductEvent('session_started', {
        page: state.currentPath,
        feature: getEventFeature('session_started', { page: state.currentPath }),
      });
    }
  } catch {
    queueProductEvent('session_started', {
      page: state.currentPath,
      feature: getEventFeature('session_started', { page: state.currentPath }),
    });
  }
};

export const initGA = (measurementId) => {
  try {
    if (!measurementId || measurementId === 'YOUR_GA_ID') {
      console.warn('GA Measurement ID is missing or default. GA will not be initialized.');
      return;
    }
    ReactGA.initialize(measurementId);
  } catch (e) {
    console.warn('GA init failed', e);
  }
};

export const initMixpanel = (token) => {
  try {
    if (!token || token === 'YOUR_MIXPANEL_TOKEN') {
      console.warn('Mixpanel Token is missing or default. Mixpanel will not be initialized.');
      return;
    }

    mixpanel.init(token, {
      debug: process.env.NODE_ENV !== 'production',
      track_pageview: false,
      persistence: 'localStorage',
      api_host: 'https://api-js.mixpanel.com',
    });
  } catch (e) {
    console.warn('Mixpanel init failed', e);
  }
};

export const attachUserProps = (params = {}) => {
  try {
    const portal = getCurrentPortal();
    const {
      user,
      boardId,
      boardName,
      gradeId,
      gradeName,
    } = resolveAnalyticsIdentityContext(portal);
    const merged = {
      ...params,
      anonymous_id: getAnonymousId(),
      analytics_session_id: getAnalyticsSessionId(),
      is_guest_session: isGuestSession(),
      user_id: user.id ?? user.user_id ?? user.pk ?? null,
      user_type: user.user_type ?? user.type ?? (portal === 'admin' ? 'superadmin' : portal) ?? null,
      username: user.username ?? user.name ?? user.full_name ?? null,
      country: user.country ?? user.country_code ?? null,
      board_id: boardId,
      board_name: boardName,
      grade_id: gradeId,
      grade_name: gradeName,
      portal,
    };

    try {
      if (mixpanel && mixpanel.identify && merged.user_id) {
        mixpanel.identify(String(merged.user_id));
        mixpanel.people.set({
          user_id: merged.user_id,
          user_type: merged.user_type,
          username: merged.username,
          country: merged.country,
          portal: merged.portal,
        });
      }
    } catch {}

    return merged;
  } catch {
    return params;
  }
};

const queueProductEvent = (name, params = {}) => {
  if (!isBrowser || !name) return;
  initProductAnalytics();

  const payload = attachUserProps(params);
  const eventId = payload.event_id || generateEventId();
  state.pendingEvents.push({
    event_id: eventId,
    name,
    occurred_at: new Date().toISOString(),
    page: getPageForEvent(payload),
    feature: getEventFeature(name, payload),
    subject_id: payload.subject_id ?? null,
    chapter_id: payload.chapter_id ?? null,
    properties: { ...payload, event_id: eventId },
  });
  state.pendingEvents = state.pendingEvents.slice(-100);
  scheduleFlush();
};

export const trackPageView = (path) => {
  if (!path) return;
  initProductAnalytics();
  captureTouch();
  state.currentPath = path;

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: document.title,
    });
  } catch {}

  try {
    if (mixpanel && mixpanel.track) {
      mixpanel.track('Page View', {
        page: path,
        title: document.title,
      });
    }
  } catch {}

  queueProductEvent('page_view', {
    page: path,
    title: document.title,
  });
};

export const trackEvent = (name, params = {}) => {
  if (!name) return;
  initProductAnalytics();
  const payload = attachUserProps(params);

  try {
    ReactGA.event(name, payload);
  } catch {
    try {
      ReactGA.event({ action: name, ...payload });
    } catch {}
  }

  try {
    if (mixpanel && mixpanel.track) mixpanel.track(name, payload);
  } catch {}

  queueProductEvent(name, payload);
};

export const identifyAnalyticsUser = ({ portal, userData, authEvent = 'identity_linked' } = {}) => {
  const currentPortal = portal || getCurrentPortal();
  const {
    boardId,
    boardName,
    gradeId,
    gradeName,
  } = resolveAnalyticsIdentityContext(currentPortal);
  const payload = {
    page: isBrowser ? getCurrentPath() : '',
    portal: currentPortal,
    user_id: userData?.id ?? null,
    user_type: userData?.user_type ?? (currentPortal === 'admin' ? 'superadmin' : currentPortal) ?? null,
    board_id: userData?.board?.id ?? userData?.board_id ?? boardId ?? null,
    board_name: userData?.board?.board_name ?? userData?.board?.board_code ?? userData?.board_name ?? boardName ?? null,
    grade_id: userData?.grade?.id ?? userData?.grade_id ?? gradeId ?? null,
    grade_name: userData?.grade?.grade_name ?? userData?.grade_name ?? gradeName ?? null,
    country: userData?.country ?? null,
    subscription_status: userData?.subscription_status ?? null,
  };

  rotateAnalyticsSession({ endCurrentSession: true })
    .catch(() => {})
    .finally(() => {
      queueProductEvent(authEvent, payload);
      flushProductAnalytics();
    });
};

export const rotateAnalyticsSession = async ({ endCurrentSession = true } = {}) => {
  if (!isBrowser) return;

  if (endCurrentSession) {
    captureEngagedTime();
    await flushProductAnalytics({ endSession: true });
  }

  if (state.flushTimer) {
    window.clearTimeout(state.flushTimer);
    state.flushTimer = null;
  }
  state.pendingEvents = [];
  state.lastFlushAt = 0;
  state.engagedMs = 0;
  state.sessionStartedAt = Date.now();
  state.lastHeartbeatAt = Date.now();
  state.lastInteractionAt = Date.now();
  state.currentPath = getCurrentPath();
  state.entryPath = state.currentPath;

  try {
    sessionStorage.removeItem(SESSION_ID_KEY);
    sessionStorage.removeItem(SESSION_STARTED_KEY);
  } catch {}

  queueProductEvent('session_started', {
    page: state.currentPath,
    feature: getEventFeature('session_started', { page: state.currentPath }),
  });
  await flushProductAnalytics();
};

export const trackApiRequest = (params = {}) => {
  queueProductEvent('api_request', params);
};

export const trackApiError = (params = {}) => {
  queueProductEvent('api_error', params);
};

export const trackContentEvent = (name, contentParams = {}) => {
  trackEvent(name, contentParams);
};

export default {
  initGA,
  initMixpanel,
  initProductAnalytics,
  trackPageView,
  trackEvent,
  attachUserProps,
  identifyAnalyticsUser,
  rotateAnalyticsSession,
  trackApiRequest,
  trackApiError,
  trackContentEvent,
};
